import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Search, Plus, FileText, Image, Upload, Trash2, Eye, Download,
  Filter, X, ChevronRight, Camera, BookOpen, Layers, ListOrdered,
  Zap, Wrench, ScanLine, Tag, Clock, User, File as FileIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = [
  { value: 'manual_electric', label: 'Manual Electric', icon: Zap, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'manual_hidraulic', label: 'Manual Hidraulic', icon: Layers, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'schema', label: 'Schema', icon: FileText, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'procedura', label: 'Procedura', icon: ListOrdered, color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'foto_procedura', label: 'Foto Procedura', icon: Camera, color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'altele', label: 'Altele', icon: FileIcon, color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

const getCategoryInfo = (cat) => CATEGORY_OPTIONS.find(c => c.value === cat) || CATEGORY_OPTIONS[5];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

export default function KnowledgeBasePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const equipmentId = searchParams.get('equipment') || '';
  const [equipment, setEquipment] = useState(null);
  const [equipmentList, setEquipmentList] = useState([]);
  const [eqSearch, setEqSearch] = useState('');
  const [showEqPicker, setShowEqPicker] = useState(!equipmentId);

  // Data
  const [documents, setDocuments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [stats, setStats] = useState(null);
  const [docTotal, setDocTotal] = useState(0);
  const [procTotal, setProcTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [activeTab, setActiveTab] = useState('documents');

  // Upload dialog
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'altele', tags: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Procedure dialog
  const [showProcDialog, setShowProcDialog] = useState(false);
  const [procForm, setProcForm] = useState({ title: '', description: '', tags: '' });
  const [creatingProc, setCreatingProc] = useState(false);

  // Procedure detail
  const [selectedProc, setSelectedProc] = useState(null);
  const [showProcDetail, setShowProcDetail] = useState(false);
  const [stepForm, setStepForm] = useState({ title: '', description: '' });
  const [stepFile, setStepFile] = useState(null);
  const [addingStep, setAddingStep] = useState(false);
  const stepFileRef = useRef(null);

  // Preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  // Fetch equipment list
  const fetchEquipmentList = useCallback(async (search = '') => {
    try {
      const params = search ? { search, limit: 20 } : { limit: 20 };
      const res = await api.get('/equipment', { params });
      setEquipmentList(res.data || []);
    } catch { setEquipmentList([]); }
  }, []);

  // Fetch equipment info
  const fetchEquipment = useCallback(async (eqId) => {
    try {
      const res = await api.get('/equipment');
      const eq = (res.data || []).find(e => e.id === eqId);
      setEquipment(eq || null);
    } catch { setEquipment(null); }
  }, []);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!equipmentId) return;
    setLoading(true);
    try {
      const params = { equipment_id: equipmentId, limit: 100 };
      if (filterCategory) params.category = filterCategory;
      if (searchText) params.search = searchText;
      const res = await api.get('/knowledge-base/documents', { params });
      setDocuments(res.data.items || []);
      setDocTotal(res.data.total || 0);
    } catch { toast.error('Eroare la incarcarea documentelor'); }
    setLoading(false);
  }, [equipmentId, filterCategory, searchText]);

  // Fetch procedures
  const fetchProcedures = useCallback(async () => {
    if (!equipmentId) return;
    try {
      const params = { equipment_id: equipmentId, limit: 100 };
      if (searchText) params.search = searchText;
      const res = await api.get('/knowledge-base/procedures', { params });
      setProcedures(res.data.items || []);
      setProcTotal(res.data.total || 0);
    } catch {}
  }, [equipmentId, searchText]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!equipmentId) return;
    try {
      const res = await api.get(`/knowledge-base/equipment/${equipmentId}/stats`);
      setStats(res.data);
    } catch {}
  }, [equipmentId]);

  useEffect(() => { fetchEquipmentList(); }, [fetchEquipmentList]);
  useEffect(() => {
    if (equipmentId) {
      fetchEquipment(equipmentId);
      fetchDocuments();
      fetchProcedures();
      fetchStats();
      setShowEqPicker(false);
    }
  }, [equipmentId, fetchEquipment, fetchDocuments, fetchProcedures, fetchStats]);

  useEffect(() => {
    if (equipmentId) {
      fetchDocuments();
      fetchProcedures();
    }
  }, [filterCategory, searchText]);

  const selectEquipment = (eq) => {
    setSearchParams({ equipment: eq.id });
    setShowEqPicker(false);
  };

  // Upload document
  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.title.trim()) {
      toast.error('Completati titlul si selectati un fisier');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('equipment_id', equipmentId);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('tags', uploadForm.tags);
      await api.post('/knowledge-base/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document incarcat cu succes!');
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ title: '', description: '', category: 'altele', tags: '' });
      fetchDocuments();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la incarcare');
    }
    setUploading(false);
  };

  // Delete document
  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Stergeti acest document?')) return;
    try {
      await api.delete(`/knowledge-base/documents/${docId}`);
      toast.success('Document sters');
      fetchDocuments();
      fetchStats();
    } catch { toast.error('Eroare la stergere'); }
  };

  // Create procedure
  const handleCreateProcedure = async () => {
    if (!procForm.title.trim()) { toast.error('Completati titlul'); return; }
    setCreatingProc(true);
    try {
      const formData = new FormData();
      formData.append('equipment_id', equipmentId);
      formData.append('title', procForm.title);
      formData.append('description', procForm.description);
      formData.append('tags', procForm.tags);
      const res = await api.post('/knowledge-base/procedures', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Procedura creata!');
      setShowProcDialog(false);
      setProcForm({ title: '', description: '', tags: '' });
      fetchProcedures();
      fetchStats();
      // Open procedure detail
      setSelectedProc(res.data);
      setShowProcDetail(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la creare');
    }
    setCreatingProc(false);
  };

  // Add step to procedure
  const handleAddStep = async () => {
    if (!stepForm.title.trim()) { toast.error('Completati titlul pasului'); return; }
    setAddingStep(true);
    try {
      const formData = new FormData();
      formData.append('title', stepForm.title);
      formData.append('description', stepForm.description);
      if (stepFile) formData.append('file', stepFile);
      await api.post(`/knowledge-base/procedures/${selectedProc.id}/steps`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Pas adaugat!');
      setStepForm({ title: '', description: '' });
      setStepFile(null);
      // Refresh procedure
      const res = await api.get(`/knowledge-base/procedures/${selectedProc.id}`);
      setSelectedProc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la adaugare pas');
    }
    setAddingStep(false);
  };

  // Delete step
  const handleDeleteStep = async (stepId) => {
    try {
      await api.delete(`/knowledge-base/procedures/${selectedProc.id}/steps/${stepId}`);
      const res = await api.get(`/knowledge-base/procedures/${selectedProc.id}`);
      setSelectedProc(res.data);
      toast.success('Pas sters');
    } catch { toast.error('Eroare la stergere pas'); }
  };

  // Delete procedure
  const handleDeleteProcedure = async (procId) => {
    if (!window.confirm('Stergeti aceasta procedura?')) return;
    try {
      await api.delete(`/knowledge-base/procedures/${procId}`);
      toast.success('Procedura stearsa');
      setShowProcDetail(false);
      setSelectedProc(null);
      fetchProcedures();
      fetchStats();
    } catch { toast.error('Eroare la stergere'); }
  };

  // Equipment picker
  if (showEqPicker && !equipmentId) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="kb-equipment-picker">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-7 h-7 text-indigo-600" />
          <h1 className="text-2xl font-bold text-slate-900">Baza de Cunostinte</h1>
        </div>
        <p className="text-slate-500 text-sm">Selectati echipamentul pentru a vizualiza documentele si procedurile.</p>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cautati echipament dupa nume sau cod..."
            value={eqSearch}
            onChange={(e) => { setEqSearch(e.target.value); fetchEquipmentList(e.target.value); }}
            className="pl-10 h-12"
            data-testid="kb-equipment-search"
            autoFocus
          />
        </div>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {equipmentList.map(eq => (
            <button
              key={eq.id}
              onClick={() => selectEquipment(eq)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors text-left"
              data-testid={`kb-eq-item-${eq.code}`}
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{eq.name}</p>
                <p className="text-xs text-slate-500">{eq.code} {eq.location_name ? `• ${eq.location_name}` : ''}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </button>
          ))}
          {equipmentList.length === 0 && (
            <p className="text-center text-slate-400 py-8">Niciun echipament gasit</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 pb-24 space-y-4" data-testid="kb-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <BookOpen className="w-6 h-6 text-indigo-600 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">Baza de Cunostinte</h1>
            {equipment && (
              <button
                onClick={() => { setSearchParams({}); setShowEqPicker(true); setEquipment(null); setDocuments([]); setProcedures([]); }}
                className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                data-testid="kb-change-equipment"
              >
                <Wrench className="w-3 h-3" /> {equipment.name} ({equipment.code})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="flex gap-2 flex-wrap" data-testid="kb-stats">
          <Badge variant="outline" className="bg-white">
            <FileText className="w-3 h-3 mr-1" /> {stats.total_documents} documente
          </Badge>
          <Badge variant="outline" className="bg-white">
            <ListOrdered className="w-3 h-3 mr-1" /> {stats.total_procedures} proceduri
          </Badge>
          {Object.entries(stats.categories || {}).map(([cat, count]) => {
            const info = getCategoryInfo(cat);
            return (
              <Badge key={cat} className={`${info.color} border text-xs`}>
                {info.label}: {count}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Search + Actions */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cautati in documente, proceduri, tag-uri..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-10"
            data-testid="kb-search-input"
          />
          {searchText && (
            <button onClick={() => setSearchText('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
        <Button onClick={() => setShowUpload(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="kb-upload-btn">
          <Upload className="w-4 h-4 mr-1" /> Document
        </Button>
        <Button onClick={() => setShowProcDialog(true)} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="kb-new-procedure-btn">
          <Plus className="w-4 h-4 mr-1" /> Procedura
        </Button>
      </div>

      {/* Tabs: Documente / Proceduri */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="documents" className="gap-1.5" data-testid="kb-tab-documents">
            <FileText className="w-4 h-4" /> Documente ({docTotal})
          </TabsTrigger>
          <TabsTrigger value="procedures" className="gap-1.5" data-testid="kb-tab-procedures">
            <ListOrdered className="w-4 h-4" /> Proceduri ({procTotal})
          </TabsTrigger>
        </TabsList>

        {/* Documents tab */}
        <TabsContent value="documents" className="space-y-3 mt-3">
          {/* Category filter chips */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilterCategory('')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                !filterCategory ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="kb-filter-all"
            >
              Toate
            </button>
            {CATEGORY_OPTIONS.map(cat => (
              <button
                key={cat.value}
                onClick={() => setFilterCategory(filterCategory === cat.value ? '' : cat.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filterCategory === cat.value ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`kb-filter-${cat.value}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-400">Se incarca...</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400">{searchText || filterCategory ? 'Niciun document gasit cu aceste filtre' : 'Niciun document incarcat inca'}</p>
              <Button onClick={() => setShowUpload(true)} variant="outline" className="mt-3">
                <Upload className="w-4 h-4 mr-1" /> Incarcati primul document
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => {
                const catInfo = getCategoryInfo(doc.category);
                const CatIcon = catInfo.icon;
                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors"
                    data-testid={`kb-doc-${doc.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${catInfo.color}`}>
                      {doc.file_type === 'pdf' ? <FileText className="w-5 h-5" /> : doc.file_type === 'image' ? <Image className="w-5 h-5" /> : <CatIcon className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate text-sm">{doc.title}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded ${catInfo.color} text-[10px]`}>{catInfo.label}</span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>{formatDate(doc.created_at)}</span>
                      </div>
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {doc.tags.map((tag, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => { setPreviewUrl(`${API_URL}${doc.file_url}`); setPreviewType(doc.file_type); }}
                        data-testid={`kb-doc-view-${doc.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <a href={`${API_URL}${doc.file_url}`} download={doc.original_file_name} target="_blank" rel="noreferrer">
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`kb-doc-download-${doc.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteDoc(doc.id)}
                        data-testid={`kb-doc-delete-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Procedures tab */}
        <TabsContent value="procedures" className="space-y-3 mt-3">
          {procedures.length === 0 ? (
            <div className="text-center py-12">
              <ListOrdered className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400">{searchText ? 'Nicio procedura gasita' : 'Nicio procedura creata inca'}</p>
              <Button onClick={() => setShowProcDialog(true)} variant="outline" className="mt-3">
                <Plus className="w-4 h-4 mr-1" /> Creati prima procedura
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {procedures.map(proc => (
                <button
                  key={proc.id}
                  onClick={() => { setSelectedProc(proc); setShowProcDetail(true); }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors text-left"
                  data-testid={`kb-proc-${proc.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <ListOrdered className="w-5 h-5 text-green-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate text-sm">{proc.title}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                      <span>{proc.steps?.length || 0} pasi</span>
                      <span>{formatDate(proc.created_at)}</span>
                      <span>{proc.created_by_name}</span>
                    </div>
                    {proc.tags && proc.tags.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {proc.tags.map((tag, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg" data-testid="kb-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" /> Incarca Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fisier *</Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setUploadFile(f);
                    if (!uploadForm.title) setUploadForm(prev => ({ ...prev, title: f.name.replace(/\.[^.]+$/, '') }));
                  }
                }}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx"
                className="mt-1 block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                data-testid="kb-upload-file-input"
              />
              {uploadFile && <p className="text-xs text-slate-500 mt-1">{uploadFile.name} ({formatFileSize(uploadFile.size)})</p>}
            </div>
            <div>
              <Label>Titlu *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Ex: Manual motor electric 3kW"
                data-testid="kb-upload-title"
              />
            </div>
            <div>
              <Label>Categorie</Label>
              <Select value={uploadForm.category} onValueChange={(v) => setUploadForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="kb-upload-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descriere</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Descriere scurta..."
                rows={2}
                data-testid="kb-upload-description"
              />
            </div>
            <div>
              <Label>Tag-uri (separate prin virgula)</Label>
              <Input
                value={uploadForm.tags}
                onChange={(e) => setUploadForm(p => ({ ...p, tags: e.target.value }))}
                placeholder="motor, 3kW, electric, pompa"
                data-testid="kb-upload-tags"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Anulare</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile} className="bg-indigo-600 hover:bg-indigo-700" data-testid="kb-upload-submit">
              {uploading ? 'Se incarca...' : 'Incarca'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Procedure Dialog */}
      <Dialog open={showProcDialog} onOpenChange={setShowProcDialog}>
        <DialogContent className="max-w-lg" data-testid="kb-procedure-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListOrdered className="w-5 h-5 text-green-600" /> Procedura Noua
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titlu *</Label>
              <Input
                value={procForm.title}
                onChange={(e) => setProcForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Ex: Procedura inlocuire filtru hidraulic"
                data-testid="kb-proc-title"
              />
            </div>
            <div>
              <Label>Descriere</Label>
              <Textarea
                value={procForm.description}
                onChange={(e) => setProcForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Descriere procedura..."
                rows={2}
                data-testid="kb-proc-description"
              />
            </div>
            <div>
              <Label>Tag-uri (separate prin virgula)</Label>
              <Input
                value={procForm.tags}
                onChange={(e) => setProcForm(p => ({ ...p, tags: e.target.value }))}
                placeholder="filtru, hidraulic, inlocuire"
                data-testid="kb-proc-tags"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProcDialog(false)}>Anulare</Button>
            <Button onClick={handleCreateProcedure} disabled={creatingProc} className="bg-green-600 hover:bg-green-700" data-testid="kb-proc-submit">
              {creatingProc ? 'Se creaza...' : 'Creaza Procedura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Procedure Detail Dialog */}
      <Dialog open={showProcDetail} onOpenChange={setShowProcDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kb-procedure-detail">
          {selectedProc && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-green-600" />
                  {selectedProc.title}
                </DialogTitle>
                <div className="text-sm text-slate-500">{selectedProc.description}</div>
                {selectedProc.tags?.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {selectedProc.tags.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                )}
              </DialogHeader>

              {/* Steps */}
              <div className="space-y-3 mt-4">
                <h3 className="font-semibold text-sm text-slate-700">Pasi ({selectedProc.steps?.length || 0})</h3>
                {(selectedProc.steps || []).map((step, idx) => (
                  <div key={step.id} className="flex gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid={`kb-step-${step.id}`}>
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {step.step_number || idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{step.title}</p>
                      {step.description && <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>}
                      {step.image_url && (
                        <img
                          src={`${API_URL}${step.image_url}`}
                          alt={step.title}
                          className="mt-2 rounded-lg max-w-full max-h-48 object-contain border border-slate-200 cursor-pointer"
                          onClick={() => { setPreviewUrl(`${API_URL}${step.image_url}`); setPreviewType('image'); }}
                        />
                      )}
                    </div>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 flex-shrink-0"
                      onClick={() => handleDeleteStep(step.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}

                {/* Add step form */}
                <div className="p-3 rounded-lg border-2 border-dashed border-slate-300 space-y-3" data-testid="kb-add-step-form">
                  <p className="text-sm font-medium text-slate-600">Adauga pas nou</p>
                  <Input
                    placeholder="Titlu pas (ex: Scoateti capacul)"
                    value={stepForm.title}
                    onChange={(e) => setStepForm(p => ({ ...p, title: e.target.value }))}
                    data-testid="kb-step-title"
                  />
                  <Textarea
                    placeholder="Descriere detaliata (optional)"
                    value={stepForm.description}
                    onChange={(e) => setStepForm(p => ({ ...p, description: e.target.value }))}
                    rows={2}
                    data-testid="kb-step-description"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => stepFileRef.current?.click()}
                      className="gap-1"
                    >
                      <Camera className="w-4 h-4" /> {stepFile ? stepFile.name : 'Adauga foto'}
                    </Button>
                    <input
                      type="file"
                      ref={stepFileRef}
                      onChange={(e) => setStepFile(e.target.files?.[0] || null)}
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      data-testid="kb-step-file-input"
                    />
                    {stepFile && (
                      <button onClick={() => setStepFile(null)} className="text-red-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Button
                    onClick={handleAddStep}
                    disabled={addingStep || !stepForm.title.trim()}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="kb-step-submit"
                  >
                    {addingStep ? 'Se adauga...' : 'Adauga Pas'}
                  </Button>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button
                  variant="destructive" size="sm"
                  onClick={() => handleDeleteProcedure(selectedProc.id)}
                  data-testid="kb-proc-delete"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Sterge Procedura
                </Button>
                <Button variant="outline" onClick={() => setShowProcDetail(false)}>Inchide</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-2" data-testid="kb-preview-dialog">
          {previewType === 'pdf' ? (
            <iframe src={previewUrl} className="w-full h-[80vh] rounded" title="PDF Preview" />
          ) : (
            <img src={previewUrl} alt="Preview" className="max-w-full max-h-[80vh] mx-auto rounded object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
